import { usePostHog } from '@posthog/react';
import Loader from 'components/Loader';
import CancelButton from 'components/buttons/CancelButton';
import DeleteButton from 'components/buttons/DeleteButton';
import SaveButton from 'components/buttons/SaveButton';
import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import React, { cloneElement, useRef } from 'react';
import { useSelector } from 'store';
import { useAddExperienceRecordMutation, useUpdateExperienceRecordMutation } from 'store/api/experience-records.ts';
import { ExperienceRecordFormData } from 'types/experience-records.ts';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState.ts';
import { experienceRecordFormToRequest } from './experienceRecordFormMapping';
import {
    ExperienceRecordCasesFields,
    ExperienceRecordConfidentialAlert,
    ExperienceRecordCoreFields,
    ExperienceRecordDescriptionFields,
    ExperienceRecordMarketingFields,
    ExperienceRecordNameField,
    ExperienceRecordTeamFields,
    ExperienceRecordValueFields,
} from './form-sections/ExperienceRecordFields';
import { useExperienceRecordForm } from './form-sections/useExperienceRecordForm';
import { useExperienceRecordDelete } from './useExperienceRecordDelete';

type Props = {
    type: 'add' | 'edit';
    initialData?: ExperienceRecordFormData;
};

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    clientParty: 'experience-records.form.general',
    clientName: 'experience-records.form.general',
    isConfidential: 'experience-records.form.general',
    date: 'experience-records.form.general',
    clientConsentRequestedOn: 'experience-records.form.general',
    clientConsentReceivedOn: 'experience-records.form.general',
    countries: 'experience-records.form.general',
    connectedBusinessAreas: 'experience-records.form.general',
    connectedSectors: 'experience-records.form.general',
    name: 'experience-records.form.details',
    description: 'experience-records.form.details',
    descriptionLocalLang: 'experience-records.form.details',
    valueAmount: 'experience-records.form.details',
    currency: 'experience-records.form.details',
    isValueConfidential: 'experience-records.form.details',
    matters: 'experience-records.form.connect-cases',
    supervisedBy: 'experience-records.form.our-team-role',
    responsibleLawyers: 'experience-records.form.our-team-role',
    handledByEmployees: 'experience-records.form.our-team-role',
    isShowcasePriority: 'experience-records.form.marketing',
    isUsedInMarketing: 'experience-records.form.marketing',
    isIncludedInLegalDirectories: 'experience-records.form.marketing',
    parties: 'experience-records.form.marketing',
};

const ExperienceRecordForm = ({ type, initialData }: Props) => {
    useFocusedEditMode('experience');
    const form = useExperienceRecordForm(initialData);
    useCreateDialogForm(form.methods);
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = form.methods;
    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);
    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const { id: xrId } = useCreateRoute().params;
    const returnTo = useReturnToLocation(xrId ? `/experience-records/${xrId}` : '/experience-records');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();
    const bypassNavigationBlockerRef = useRef(false);

    const posthog = usePostHog();
    const [addExperienceRecord] = useAddExperienceRecordMutation();
    const [updateExperienceRecord] = useUpdateExperienceRecordMutation();
    const handleDelete = useExperienceRecordDelete(xrId, () => {
        bypassNavigationBlockerRef.current = true;
    });

    if (form.isLoading) return <Loader />;

    const getFormTitle = () =>
        type === 'edit' ? t('experience-records.form.edit-title') : t('experience-records.form.add-title');

    const onSubmit = async (data: ExperienceRecordFormData, action: 'create' | 'update') => {
        try {
            if (action === 'create') {
                const newXr = await addExperienceRecord(experienceRecordFormToRequest(data)).unwrap();
                posthog.capture('experience_record_created');
                bypassNavigationBlockerRef.current = true;
                leaveForm(`/experience-records/${newXr.id}`);
            } else if (action === 'update' && xrId) {
                await updateExperienceRecord({ ...experienceRecordFormToRequest(data), id: Number(xrId) }).unwrap();
                bypassNavigationBlockerRef.current = true;
                leaveForm(`/experience-records/${xrId}`);
            }
        } catch (e) {
            logError(e);
            console.error(e);
        }
    };

    const submitCreate = () => {
        void handleSubmit((data) => onSubmit(data, 'create'))();
    };
    const submitUpdate = () => {
        void handleSubmit((data) => onSubmit(data, 'update'))();
    };

    const getFormHeader = () => {
        const actions: React.ReactElement[] = [];

        if (type === 'add') {
            actions.push(
                <CancelButton onClick={goBack} />,
                <SaveButton onClick={submitCreate} disabled={buttonsDisabled} />
            );
        } else if (type === 'edit') {
            actions.push(
                <CancelButton onClick={goBack} />,
                <DeleteButton
                    permissions={['DELETE.EXPERIENCE_RECORDS.ALL']}
                    onClick={handleDelete}
                    disabled={buttonsDisabled}
                />,
                <SaveButton
                    permissions={['UPDATE.EXPERIENCE_RECORDS.ALL']}
                    onClick={submitUpdate}
                    disabled={buttonsDisabled}
                />
            );
        }

        return (
            <ActionHeader
                title={getFormTitle()}
                actions={actions.map((button, index) => cloneElement(button, { key: `action#${index}` }))}
                onBack={goBack}
            />
        );
    };

    return (
        <SectionActionsProvider>
            <HighlightedFieldsProvider>
                <FormWrapper>
                    {getFormHeader()}
                    <SectionLayout sectionErrors={sectionErrors}>
                        <Section title="experience-records.form.general" collapsible>
                            <ExperienceRecordCoreFields form={form} />
                        </Section>
                        <Section title="experience-records.form.details" collapsible>
                            <ExperienceRecordConfidentialAlert form={form} />
                            <ExperienceRecordNameField form={form} />
                            <ExperienceRecordDescriptionFields form={form} />
                            <ExperienceRecordValueFields form={form} />
                        </Section>
                        <Section title="experience-records.form.connect-cases" collapsible>
                            <ExperienceRecordCasesFields form={form} />
                        </Section>
                        <Section title="experience-records.form.our-team-role" collapsible>
                            <ExperienceRecordTeamFields form={form} />
                        </Section>
                        <Section title="experience-records.form.marketing" collapsible>
                            <ExperienceRecordMarketingFields form={form} />
                        </Section>
                    </SectionLayout>
                    <ConfirmNavigation control={control} bypassRef={bypassNavigationBlockerRef} />
                </FormWrapper>
            </HighlightedFieldsProvider>
        </SectionActionsProvider>
    );
};

export default ExperienceRecordForm;
